/**
 * React Query hooks for Media Library operations.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import * as mediaApi from '@/services/api/media';
import type {
  ListImagesRequest,
  CreateFolderRequest,
  CreateTagRequest,
  UpdateImageRequest,
  ImageCategory,
} from '@/types/media';

// Query keys
export const mediaQueryKeys = {
  all: ['media'] as const,
  folders: (memberUuid: string) => [...mediaQueryKeys.all, 'folders', memberUuid] as const,
  tags: (memberUuid: string) => [...mediaQueryKeys.all, 'tags', memberUuid] as const,
  images: (memberUuid: string, params?: Partial<ListImagesRequest>) =>
    [...mediaQueryKeys.all, 'images', memberUuid, params] as const,
  image: (memberUuid: string, imageId: string) =>
    [...mediaQueryKeys.all, 'image', memberUuid, imageId] as const,
  storage: (memberUuid: string) => [...mediaQueryKeys.all, 'storage', memberUuid] as const,
};

// =============================================================================
// Folder Queries
// =============================================================================

export function useMediaFolders() {
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useQuery({
    queryKey: mediaQueryKeys.folders(memberUuid),
    queryFn: () => mediaApi.listFolders(memberUuid),
    enabled: !!memberUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: (data: Omit<CreateFolderRequest, 'member_uuid'>) =>
      mediaApi.createFolder({ ...data, member_uuid: memberUuid }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.folders(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.storage(memberUuid) });
    },
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: (folderId: string) => mediaApi.deleteFolder(folderId, memberUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.folders(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.images(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.storage(memberUuid) });
    },
  });
}

// =============================================================================
// Tag Queries
// =============================================================================

export function useMediaTags() {
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useQuery({
    queryKey: mediaQueryKeys.tags(memberUuid),
    queryFn: () => mediaApi.listTags(memberUuid),
    enabled: !!memberUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: (data: Omit<CreateTagRequest, 'member_uuid'>) =>
      mediaApi.createTag({ ...data, member_uuid: memberUuid }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.tags(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.storage(memberUuid) });
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: (tagId: string) => mediaApi.deleteTag(tagId, memberUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.tags(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.images(memberUuid) });
    },
  });
}

// =============================================================================
// Image Queries
// =============================================================================

export function useMediaImages(params?: Partial<Omit<ListImagesRequest, 'member_uuid'>>) {
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useQuery({
    queryKey: mediaQueryKeys.images(memberUuid, params),
    queryFn: () =>
      mediaApi.listImages({
        member_uuid: memberUuid,
        page: 1,
        page_size: 20,
        sort_by: 'created_at',
        sort_order: 'desc',
        ...params,
      }),
    enabled: !!memberUuid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

export function useMediaImage(imageId: string) {
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useQuery({
    queryKey: mediaQueryKeys.image(memberUuid, imageId),
    queryFn: () => mediaApi.getImage(imageId, memberUuid),
    enabled: !!memberUuid && !!imageId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

export function useUploadImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      options,
    }: {
      file: File;
      options?: {
        folderId?: string;
        altText?: string;
        description?: string;
        tagIds?: string[];
        category?: Exclude<ImageCategory, 'targeted'>;
      };
    }) => {
      // Get memberUuid at mutation time, not hook creation time
      const memberUuid = useAuthStore.getState().memberUuid;
      if (!memberUuid) {
        throw new Error('Not authenticated');
      }
      const response = await mediaApi.uploadImage(file, memberUuid, options);
      // Check if the upload was successful
      if (!response.success) {
        throw new Error(response.message || 'Upload failed');
      }
      return response;
    },
    onSuccess: () => {
      const memberUuid = useAuthStore.getState().memberUuid || '';
      // Use prefix matching by invalidating the base 'images' key
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.storage(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.folders(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.tags(memberUuid) });
    },
  });
}

export function useUpdateImage() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: ({
      imageId,
      data,
    }: {
      imageId: string;
      data: Omit<UpdateImageRequest, 'member_uuid'>;
    }) => mediaApi.updateImage(imageId, { ...data, member_uuid: memberUuid }),
    onSuccess: (_, { imageId }) => {
      // Use prefix matching to invalidate all image queries regardless of filter params
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.image(memberUuid, imageId) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.folders(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.tags(memberUuid) });
    },
  });
}

export function useDeleteImage() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: (imageId: string) => mediaApi.deleteImage(imageId, memberUuid),
    onSuccess: () => {
      // Use prefix matching to invalidate all image queries regardless of filter params
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.storage(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.folders(memberUuid) });
    },
  });
}

export function useSetImageCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    // Read `memberUuid` at mutation time (not hook creation) — same shape as
    // `useUploadImage`. Capturing at hook creation would default to `''` if
    // the auth store hadn't hydrated yet, sending an empty `member_uuid` to
    // the backend on the first call after a refresh.
    mutationFn: ({
      imageId,
      category,
    }: {
      imageId: string;
      category: Exclude<ImageCategory, 'targeted'>;
    }) => {
      const memberUuid = useAuthStore.getState().memberUuid;
      if (!memberUuid) {
        throw new Error('Not authenticated');
      }
      return mediaApi.setImageCategory(imageId, { member_uuid: memberUuid, category });
    },
    onSuccess: (_, { imageId }) => {
      const memberUuid = useAuthStore.getState().memberUuid || '';
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.image(memberUuid, imageId) });
    },
  });
}

// =============================================================================
// Batch Operations
// =============================================================================

export function useBatchDeleteImages() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: (imageIds: string[]) =>
      mediaApi.batchDeleteImages({ member_uuid: memberUuid, image_ids: imageIds }),
    onSuccess: () => {
      // Use prefix matching to invalidate all image queries regardless of filter params
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.storage(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.folders(memberUuid) });
    },
  });
}

export function useBatchMoveImages() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: ({ imageIds, folderId }: { imageIds: string[]; folderId: string | null }) =>
      mediaApi.batchMoveImages({ member_uuid: memberUuid, image_ids: imageIds, folder_id: folderId }),
    onSuccess: () => {
      // Use prefix matching to invalidate all image queries regardless of filter params
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.folders(memberUuid) });
    },
  });
}

export function useBatchTagImages() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: ({ imageIds, tagIds }: { imageIds: string[]; tagIds: string[] }) =>
      mediaApi.batchTagImages({ member_uuid: memberUuid, image_ids: imageIds, tag_ids: tagIds }),
    onSuccess: () => {
      // Use prefix matching to invalidate all image queries regardless of filter params
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.tags(memberUuid) });
    },
  });
}

// =============================================================================
// Storage Summary
// =============================================================================

export function useStorageSummary() {
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useQuery({
    queryKey: mediaQueryKeys.storage(memberUuid),
    queryFn: () => mediaApi.getStorageSummary(memberUuid),
    enabled: !!memberUuid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// =============================================================================
// Usage Tracking Hooks
// =============================================================================

export function useLinkImageToEntity() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: ({
      imageId,
      data,
    }: {
      imageId: string;
      data: Omit<mediaApi.LinkImageRequest, 'member_uuid'>;
    }) => mediaApi.linkImageToEntity(imageId, { ...data, member_uuid: memberUuid }),
    onSuccess: (_, { imageId }) => {
      // Use prefix matching to invalidate all image queries regardless of filter params
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.image(memberUuid, imageId) });
    },
  });
}

export function useUnlinkImageFromEntity() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: ({ imageId, usageId }: { imageId: string; usageId: string }) =>
      mediaApi.unlinkImageFromEntity(imageId, usageId, memberUuid),
    onSuccess: (_, { imageId }) => {
      // Use prefix matching to invalidate all image queries regardless of filter params
      queryClient.invalidateQueries({ queryKey: [...mediaQueryKeys.all, 'images', memberUuid] });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.image(memberUuid, imageId) });
    },
  });
}

export function useImagesByEntity(params: Omit<mediaApi.GetImagesByEntityRequest, 'member_uuid'>) {
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useQuery({
    queryKey: [...mediaQueryKeys.all, 'by-entity', memberUuid, params],
    queryFn: () => mediaApi.getImagesByEntity({ ...params, member_uuid: memberUuid }),
    enabled: !!memberUuid && !!params.usage_type,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}
